import type { CellStack, MegaBlockDef, BlockadeWallDef } from '../types/board';
import { cellKey } from '../types/board';

export interface ReachabilityOptions {
  /** Cell keys behind locked doors (shots pass through). */
  doorCells?: Set<number>;
  /** Cell keys behind barricades (shots pass through). */
  barricadeCells?: Set<number>;
  /** Cell keys that are snake blocks (block horizontal/up, hittable from above). */
  snakeCells?: Set<number>;
  /** Mega block definitions -- their cells are treated as a single target per block. */
  megaBlockDefs?: MegaBlockDef[];
  /** Blockade wall definitions -- walls block shots from one edge. */
  blockadeWalls?: BlockadeWallDef[];
}

export interface ColorReachability {
  reachable: number;
  total: number;
}

/**
 * Compute color reachability for a board grid.
 *
 * For each color, determines:
 * - `total`: all cubes of that color on the board (including mega block HP)
 * - `reachable`: cubes that can be hit by at least one shot from any edge
 *
 * A cube is "reachable" if it is the FIRST non-empty, non-skipped cell along
 * at least one firing line from any of the 4 edges. This matches the logic
 * in ShootingSystem.findPerpendicularTarget().
 *
 * Skipped cells: locked door cells, barricade cells (shots pass through).
 * Snake cells: block horizontal and upward shots; hittable only from above.
 * Blockade walls: block all shots from one edge along covered rows/cols.
 */
export function computeColorReachability(
  grid: CellStack[][],
  options: ReachabilityOptions = {},
): Map<number, ColorReachability> {
  const rows = grid.length;
  if (rows === 0) return new Map();
  const cols = grid[0].length;
  if (cols === 0) return new Map();

  const doorCells = options.doorCells ?? new Set();
  const barricadeCells = options.barricadeCells ?? new Set();
  const snakeCells = options.snakeCells ?? new Set();
  const megaBlockDefs = options.megaBlockDefs ?? [];
  const blockadeWalls = options.blockadeWalls ?? [];

  // Build mega block cell set
  const megaCells = new Set<number>();
  for (const def of megaBlockDefs) {
    for (let r = def.row; r < def.row + def.height; r++) {
      for (let c = def.col; c < def.col + def.width; c++) {
        megaCells.add(cellKey(r, c));
      }
    }
  }

  // Build blockade wall lookup: side -> set of blocked cell indices
  const wallBlocked = {
    left: new Set<number>(),   // blocked rows when firing right
    right: new Set<number>(),  // blocked rows when firing left
    top: new Set<number>(),    // blocked cols when firing down
    bottom: new Set<number>(), // blocked cols when firing up
  };
  for (const wall of blockadeWalls) {
    const set = wallBlocked[wall.side];
    for (let i = wall.startCell; i < wall.startCell + wall.length; i++) {
      set.add(i);
    }
  }

  // Count total cubes per color (excluding mega block cells, then adding mega HP)
  const totalByColor = new Map<number, number>();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (megaCells.has(cellKey(r, c))) continue;
      for (const color of grid[r][c]) {
        totalByColor.set(color, (totalByColor.get(color) ?? 0) + 1);
      }
    }
  }
  for (const def of megaBlockDefs) {
    totalByColor.set(def.colorIndex, (totalByColor.get(def.colorIndex) ?? 0) + def.hp);
  }

  // Track which cells are reachable (can be the first target along at least one line)
  const reachableCells = new Set<number>();
  // Track which mega blocks are reachable
  const reachableMegaBlocks = new Set<number>(); // index into megaBlockDefs

  const isSkipped = (r: number, c: number): boolean => {
    const key = cellKey(r, c);
    return doorCells.has(key) || barricadeCells.has(key);
  };

  const isSnake = (r: number, c: number): boolean => {
    return snakeCells.has(cellKey(r, c));
  };

  const hasContent = (r: number, c: number): boolean => {
    return grid[r][c].length > 0;
  };

  const markMegaOrCell = (r: number, c: number): void => {
    const key = cellKey(r, c);
    if (megaCells.has(key)) {
      const mbIdx = megaBlockDefs.findIndex(
        def => r >= def.row && r < def.row + def.height && c >= def.col && c < def.col + def.width
      );
      if (mbIdx >= 0) reachableMegaBlocks.add(mbIdx);
    } else {
      reachableCells.add(key);
    }
  };

  // Scan from all 4 edges, find first hittable cell per firing line

  // Firing RIGHT (attacker on left edge, shots go left-to-right)
  // Wall side that blocks: 'left'
  for (let r = 0; r < rows; r++) {
    if (wallBlocked.left.has(r)) continue; // wall blocks entire line
    for (let c = 0; c < cols; c++) {
      if (isSkipped(r, c)) continue;
      if (!hasContent(r, c)) continue;
      // Snake cells block horizontal shots
      if (isSnake(r, c)) break;
      markMegaOrCell(r, c);
      break; // only first cell per line
    }
  }

  // Firing LEFT (attacker on right edge, shots go right-to-left)
  // Wall side that blocks: 'right'
  for (let r = 0; r < rows; r++) {
    if (wallBlocked.right.has(r)) continue;
    for (let c = cols - 1; c >= 0; c--) {
      if (isSkipped(r, c)) continue;
      if (!hasContent(r, c)) continue;
      if (isSnake(r, c)) break;
      markMegaOrCell(r, c);
      break;
    }
  }

  // Firing DOWN (attacker on top edge, shots go top-to-bottom)
  // Wall side that blocks: 'top'
  for (let c = 0; c < cols; c++) {
    if (wallBlocked.top.has(c)) continue;
    for (let r = 0; r < rows; r++) {
      if (isSkipped(r, c)) continue;
      if (!hasContent(r, c)) continue;
      // Snake cells CAN be hit from above -- no break here
      markMegaOrCell(r, c);
      break;
    }
  }

  // Firing UP (attacker on bottom edge, shots go bottom-to-top)
  // Wall side that blocks: 'bottom'
  for (let c = 0; c < cols; c++) {
    if (wallBlocked.bottom.has(c)) continue;
    for (let r = rows - 1; r >= 0; r--) {
      if (isSkipped(r, c)) continue;
      if (!hasContent(r, c)) continue;
      // Snake cells block upward shots
      if (isSnake(r, c)) break;
      markMegaOrCell(r, c);
      break;
    }
  }

  // Count reachable cubes per color from reachableCells
  const reachableByColor = new Map<number, number>();
  for (const key of reachableCells) {
    const r = Math.floor(key / 256);
    const c = key % 256;
    const topColor = grid[r][c].length > 0 ? grid[r][c][grid[r][c].length - 1] : null;
    if (topColor !== null) {
      reachableByColor.set(topColor, (reachableByColor.get(topColor) ?? 0) + 1);
    }
  }

  // Add mega block reachability (entire HP is "reachable" if the block is reachable)
  for (const mbIdx of reachableMegaBlocks) {
    const def = megaBlockDefs[mbIdx];
    reachableByColor.set(def.colorIndex, (reachableByColor.get(def.colorIndex) ?? 0) + def.hp);
  }

  // Build result map
  const result = new Map<number, ColorReachability>();
  for (const [color, total] of totalByColor) {
    result.set(color, {
      reachable: Math.min(reachableByColor.get(color) ?? 0, total),
      total,
    });
  }

  return result;
}
